const MEGABYTE = 1024 * 1024
const MAX_SOURCE_PIXELS = 32 * 1000 * 1000
const MAX_LARGE_SOURCE_PIXELS = 80 * 1000 * 1000
const IMAGE_METADATA_READ_BYTES = MEGABYTE
const MINIMUM_SIZE_REDUCTION = 0.1
const HEIC_EXTENSIONS = ['heic', 'heif']
let imageBitmapResizeSupportPromise = null

export const MAX_SOURCE_IMAGE_BYTES = 25 * MEGABYTE
export const MAX_UPLOAD_IMAGE_BYTES = 10 * MEGABYTE

const IMAGE_PROFILES = {
  luggage: {
    maxDimension: 1600,
    quality: 0.82,
    minimumSize: 750 * 1024
  },
  barcode: {
    maxDimension: 2560,
    quality: 0.92,
    minimumSize: MEGABYTE
  }
}

function getProfile (photoType) {
  return IMAGE_PROFILES[photoType] || IMAGE_PROFILES.luggage
}

function createOptimizationError (code, message) {
  const error = new Error(message)
  error.code = code
  return error
}

function getFileExtension (file) {
  const fileName = file.name || ''
  const lastDotIndex = fileName.lastIndexOf('.')

  return lastDotIndex === -1 ? '' : fileName.slice(lastDotIndex + 1).toLowerCase()
}

function isHeicFile (file) {
  return HEIC_EXTENSIONS.includes(getFileExtension(file)) ||
    file.type === 'image/heic' ||
    file.type === 'image/heif'
}

function readBlobAsArrayBuffer (blob) {
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader()

    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(createOptimizationError(
      'metadata_read_failed',
      'Image metadata could not be read'
    ))
    reader.readAsArrayBuffer(blob)
  })
}

function hasBytes (bytes, offset, expectedBytes) {
  return expectedBytes.every((expectedByte, index) => bytes[offset + index] === expectedByte)
}

function parsePngDimensions (bytes, view) {
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

  if (bytes.length < 24 || !hasBytes(bytes, 0, pngSignature)) {
    return null
  }

  return {
    width: view.getUint32(16),
    height: view.getUint32(20)
  }
}

function parseJpegDimensions (bytes, view) {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return null
  }

  const startOfFrameMarkers = [
    0xc0, 0xc1, 0xc2, 0xc3,
    0xc5, 0xc6, 0xc7,
    0xc9, 0xca, 0xcb,
    0xcd, 0xce, 0xcf
  ]
  let offset = 2

  while (offset + 3 < bytes.length) {
    while (offset < bytes.length && bytes[offset] !== 0xff) {
      offset += 1
    }

    while (offset < bytes.length && bytes[offset] === 0xff) {
      offset += 1
    }

    if (offset >= bytes.length) {
      break
    }

    const marker = bytes[offset]
    offset += 1

    if (marker === 0xd9 || marker === 0xda) {
      break
    }

    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      continue
    }

    if (offset + 1 >= bytes.length) {
      break
    }

    const segmentLength = view.getUint16(offset)

    if (segmentLength < 2 || offset + segmentLength > bytes.length) {
      break
    }

    if (startOfFrameMarkers.includes(marker) && segmentLength >= 7) {
      return {
        width: view.getUint16(offset + 5),
        height: view.getUint16(offset + 3)
      }
    }

    offset += segmentLength
  }

  return null
}

function parseGifDimensions (bytes, view) {
  const isGif = bytes.length >= 10 &&
    hasBytes(bytes, 0, [0x47, 0x49, 0x46, 0x38]) &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61

  if (!isGif) {
    return null
  }

  return {
    width: view.getUint16(6, true),
    height: view.getUint16(8, true)
  }
}

function parseWebpDimensions (bytes, view) {
  const isWebp = bytes.length >= 30 &&
    hasBytes(bytes, 0, [0x52, 0x49, 0x46, 0x46]) &&
    hasBytes(bytes, 8, [0x57, 0x45, 0x42, 0x50])

  if (!isWebp) {
    return null
  }

  let offset = 12

  while (offset + 8 <= bytes.length) {
    const chunkSize = view.getUint32(offset + 4, true)
    const dataOffset = offset + 8

    if (hasBytes(bytes, offset, [0x56, 0x50, 0x38, 0x58]) && chunkSize >= 10 && dataOffset + 10 <= bytes.length) {
      return {
        width: 1 + bytes[dataOffset + 4] + (bytes[dataOffset + 5] * 256) + (bytes[dataOffset + 6] * 65536),
        height: 1 + bytes[dataOffset + 7] + (bytes[dataOffset + 8] * 256) + (bytes[dataOffset + 9] * 65536)
      }
    }

    if (
      hasBytes(bytes, offset, [0x56, 0x50, 0x38, 0x20]) &&
      chunkSize >= 10 &&
      dataOffset + 10 <= bytes.length &&
      hasBytes(bytes, dataOffset + 3, [0x9d, 0x01, 0x2a])
    ) {
      return {
        width: view.getUint16(dataOffset + 6, true) & 0x3fff,
        height: view.getUint16(dataOffset + 8, true) & 0x3fff
      }
    }

    if (
      hasBytes(bytes, offset, [0x56, 0x50, 0x38, 0x4c]) &&
      chunkSize >= 5 &&
      dataOffset + 5 <= bytes.length &&
      bytes[dataOffset] === 0x2f
    ) {
      const sizeBits = view.getUint32(dataOffset + 1, true)

      return {
        width: (sizeBits & 0x3fff) + 1,
        height: ((sizeBits >>> 14) & 0x3fff) + 1
      }
    }

    offset += 8 + chunkSize + (chunkSize % 2)
  }

  return null
}

function parseHeicDimensions (bytes, view) {
  let largestDimensions = null

  for (let offset = 4; offset + 16 <= bytes.length; offset += 1) {
    if (!hasBytes(bytes, offset, [0x69, 0x73, 0x70, 0x65])) {
      continue
    }

    const boxStart = offset - 4
    const boxSize = view.getUint32(boxStart)

    if (boxSize < 20 || boxStart + boxSize > bytes.length) {
      continue
    }

    const width = view.getUint32(offset + 8)
    const height = view.getUint32(offset + 12)
    const pixelCount = width * height
    const largestPixelCount = largestDimensions
      ? largestDimensions.width * largestDimensions.height
      : 0

    if (
      width > 0 &&
      height > 0 &&
      width <= 100000 &&
      height <= 100000 &&
      pixelCount > largestPixelCount
    ) {
      largestDimensions = { width, height }
    }
  }

  return largestDimensions
}

function parseEncodedDimensions (buffer, file) {
  const bytes = new Uint8Array(buffer)
  const view = new DataView(buffer)

  return parsePngDimensions(bytes, view) ||
    parseJpegDimensions(bytes, view) ||
    parseGifDimensions(bytes, view) ||
    parseWebpDimensions(bytes, view) ||
    (isHeicFile(file) ? parseHeicDimensions(bytes, view) : null)
}

async function readEncodedDimensions (file) {
  const metadataSize = Math.min(file.size, IMAGE_METADATA_READ_BYTES)
  const headerBuffer = await readBlobAsArrayBuffer(file.slice(0, metadataSize))
  const headerDimensions = parseEncodedDimensions(headerBuffer, file)

  if (headerDimensions || !isHeicFile(file) || file.size <= metadataSize) {
    return headerDimensions
  }

  const footerBuffer = await readBlobAsArrayBuffer(file.slice(file.size - metadataSize))
  return parseEncodedDimensions(footerBuffer, file)
}

function exceedsPixelLimit (dimensions, pixelLimit = MAX_SOURCE_PIXELS) {
  return dimensions &&
    dimensions.width > 0 &&
    dimensions.height > 0 &&
    dimensions.width * dimensions.height > pixelLimit
}

function getScaledDimensions (sourceWidth, sourceHeight, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))

  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale))
  }
}

function supportsImageBitmapResize () {
  if (typeof window.createImageBitmap !== 'function') {
    return Promise.resolve(false)
  }

  if (!imageBitmapResizeSupportPromise) {
    imageBitmapResizeSupportPromise = (async () => {
      let testBitmap = null

      try {
        const testCanvas = window.document.createElement('canvas')
        testCanvas.width = 2
        testCanvas.height = 2

        testBitmap = await window.createImageBitmap(testCanvas, {
          resizeWidth: 1,
          resizeHeight: 1,
          resizeQuality: 'low'
        })

        return testBitmap.width === 1 && testBitmap.height === 1
      } catch (error) {
        return false
      } finally {
        if (testBitmap && typeof testBitmap.close === 'function') {
          testBitmap.close()
        }
      }
    })()
  }

  return imageBitmapResizeSupportPromise
}

async function loadLargeImage (file, dimensions, profile) {
  const resizeSupported = await supportsImageBitmapResize()

  if (!resizeSupported) {
    throw createOptimizationError(
      'large_image_resize_unsupported',
      'Large image optimization is not supported by this browser'
    )
  }

  const outputDimensions = getScaledDimensions(
    dimensions.width,
    dimensions.height,
    profile.maxDimension
  )
  let imageBitmap = null

  try {
    imageBitmap = await window.createImageBitmap(file, {
      resizeWidth: outputDimensions.width,
      resizeHeight: outputDimensions.height,
      resizeQuality: 'high'
    })
  } catch (error) {
    throw createOptimizationError(
      'large_image_decode_failed',
      'Large image could not be optimized by this browser'
    )
  }

  if (
    !imageBitmap.width ||
    !imageBitmap.height ||
    Math.max(imageBitmap.width, imageBitmap.height) > profile.maxDimension
  ) {
    if (typeof imageBitmap.close === 'function') {
      imageBitmap.close()
    }

    throw createOptimizationError(
      'large_image_resize_failed',
      'Large image was not resized by this browser'
    )
  }

  return {
    image: imageBitmap,
    sourceWidth: imageBitmap.width,
    sourceHeight: imageBitmap.height,
    imageBitmap
  }
}

function loadImage (file) {
  return new Promise((resolve, reject) => {
    const objectUrl = window.URL.createObjectURL(file)
    const image = new window.Image()

    image.onload = () => {
      resolve({ image, objectUrl })
    }

    image.onerror = () => {
      window.URL.revokeObjectURL(objectUrl)
      reject(createOptimizationError(
        'decode_failed',
        'Image format cannot be processed by this browser'
      ))
    }

    image.src = objectUrl
  })
}

function canvasToBlob (canvas, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== 'function') {
      reject(createOptimizationError(
        'canvas_unsupported',
        'Image optimization is not supported by this browser'
      ))
      return
    }

    canvas.toBlob(blob => {
      if (!blob) {
        reject(createOptimizationError('encode_failed', 'Image optimization failed'))
        return
      }

      resolve(blob)
    }, 'image/jpeg', quality)
  })
}

function getOutputFileName (originalName) {
  const baseName = originalName.replace(/\.[^/.]+$/, '') || 'luggage-photo'
  return `${baseName}.jpg`
}

export async function optimizeImageForUpload (file, photoType) {
  const profile = getProfile(photoType)

  let imageData = null
  let canvas = null

  try {
    const encodedDimensions = await readEncodedDimensions(file)

    // A highly compressed image can still require hundreds of MB to decode.
    if (file.size <= profile.minimumSize && encodedDimensions && !exceedsPixelLimit(encodedDimensions)) {
      return { file, optimized: false }
    }

    if (exceedsPixelLimit(encodedDimensions, MAX_LARGE_SOURCE_PIXELS)) {
      return {
        file,
        optimized: false,
        fallbackReason: 'pixel_limit',
        shouldReportFallback: true
      }
    }

    if (exceedsPixelLimit(encodedDimensions)) {
      imageData = await loadLargeImage(file, encodedDimensions, profile)
    } else {
      imageData = await loadImage(file)
    }

    const sourceWidth = imageData.sourceWidth || imageData.image.naturalWidth
    const sourceHeight = imageData.sourceHeight || imageData.image.naturalHeight

    if (!sourceWidth || !sourceHeight) {
      throw createOptimizationError('invalid_dimensions', 'Image dimensions could not be read')
    }

    if (exceedsPixelLimit({ width: sourceWidth, height: sourceHeight })) {
      return {
        file,
        optimized: false,
        fallbackReason: 'pixel_limit',
        shouldReportFallback: true
      }
    }

    const outputDimensions = getScaledDimensions(
      sourceWidth,
      sourceHeight,
      profile.maxDimension
    )
    const outputWidth = outputDimensions.width
    const outputHeight = outputDimensions.height

    canvas = window.document.createElement('canvas')
    canvas.width = outputWidth
    canvas.height = outputHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw createOptimizationError(
        'canvas_unsupported',
        'Image optimization is not supported by this browser'
      )
    }

    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, outputWidth, outputHeight)
    context.drawImage(imageData.image, 0, 0, outputWidth, outputHeight)

    const optimizedBlob = await canvasToBlob(canvas, profile.quality)

    const maximumUsefulSize = file.size * (1 - MINIMUM_SIZE_REDUCTION)

    if (!optimizedBlob.size || optimizedBlob.size > maximumUsefulSize) {
      return {
        file,
        optimized: false,
        fallbackReason: exceedsPixelLimit(encodedDimensions) ? 'large_original' : undefined
      }
    }

    const optimizedFile = new window.File(
      [optimizedBlob],
      getOutputFileName(file.name),
      {
        type: 'image/jpeg',
        lastModified: Date.now()
      }
    )

    return { file: optimizedFile, optimized: true }
  } catch (error) {
    const fallbackReason = error && error.code ? error.code : 'unexpected_error'
    const expectedHeicFallback = fallbackReason === 'decode_failed' && isHeicFile(file)

    return {
      file,
      optimized: false,
      fallbackReason,
      shouldReportFallback: !expectedHeicFallback
    }
  } finally {
    if (imageData && imageData.objectUrl) {
      window.URL.revokeObjectURL(imageData.objectUrl)
    }

    if (imageData && imageData.imageBitmap && typeof imageData.imageBitmap.close === 'function') {
      imageData.imageBitmap.close()
    }

    if (canvas) {
      canvas.width = 1
      canvas.height = 1
    }
  }
}
